import { cn } from "@/lib/utils"
import { ScalarValuePill } from "../pills"
import type { NodeValueRailConfig } from "./node-appearance"

const SLOT_COUNT = 9
const FILLED_SLOT_INDEXES = [0, 4, 8] as const

type NodeValueRailProps = {
	config: NodeValueRailConfig
	onParameterChange: (paramName: string, value: unknown) => void
	className?: string
	selected?: boolean
}

export function NodeValueRail({
	config,
	onParameterChange,
	className,
	selected = false,
}: NodeValueRailProps) {
	const minValue = Number(config.minParam.value)
	const maxValue = Number(config.maxParam.value)
	const currentValue = Number(config.valueParam.value)
	const ratio =
		Number.isFinite(minValue) &&
		Number.isFinite(maxValue) &&
		Number.isFinite(currentValue) &&
		maxValue !== minValue
			? Math.min(
					1,
					Math.max(0, (currentValue - minValue) / (maxValue - minValue)),
				)
			: 0.5
	const clampedPercent = Math.min(88, Math.max(12, ratio * 100))
	const slotValues = new Map<number, typeof config.minParam>([
		[FILLED_SLOT_INDEXES[0], config.minParam],
		[FILLED_SLOT_INDEXES[1], config.valueParam],
		[FILLED_SLOT_INDEXES[2], config.maxParam],
	])

	return (
		<div className={cn("px-[10px] py-[5px]", className)}>
			<div
				className="relative h-[34px] overflow-hidden rounded-[20px]"
				style={{
					backgroundColor: selected
						? "color-mix(in srgb, var(--node-selection) 22%, var(--node-rail-track))"
						: "var(--node-rail-track)",
					boxShadow: "var(--node-pill-shadow)",
				}}
			>
				<div
					className="absolute inset-y-0 left-0 rounded-bl-[20px] rounded-br-[5px] rounded-tl-[20px] rounded-tr-[5px]"
					style={{
						width: `${clampedPercent}%`,
						backgroundColor: selected
							? "rgba(255,255,255,0.7)"
							: "var(--node-rail-active)",
					}}
				/>
				<div
					className="absolute inset-0"
					style={{
						backgroundImage:
							"repeating-linear-gradient(to right, transparent 0 calc(10% - 1px), var(--node-divider) calc(10% - 1px) 10%)",
					}}
				/>
				{Array.from({ length: SLOT_COUNT }, (_, index) => {
					const parameter = slotValues.get(index)
					const isEmphasized = parameter?.name === config.valueParam.name

					return (
						<div
							key={parameter?.name ?? `slot:${index}`}
							className={cn(
								"absolute inset-y-0 flex items-center justify-center",
								index === 0 && "left-0 w-14 justify-start pl-3",
								index === FILLED_SLOT_INDEXES[1] &&
									"w-[72px] -translate-x-1/2 justify-center",
								index === SLOT_COUNT - 1 && "right-0 w-14 justify-end pr-3",
								!parameter && "hidden",
							)}
							style={
								index === FILLED_SLOT_INDEXES[1]
									? { left: `${clampedPercent}%` }
									: undefined
							}
						>
							{parameter ? (
								<ScalarValuePill
									value={parameter.value}
									type={parameter.type}
									variant="rail"
									emphasized={isEmphasized}
									onChange={(value) => onParameterChange(parameter.name, value)}
								/>
							) : null}
						</div>
					)
				})}
			</div>
		</div>
	)
}
